import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why I won't be switching to VSCode any time soon",
  "description": "",
  "published": "2017-11-03T00:00:00.000Z",
  "redirect_from": ["/blog/wont-be-switching-vscode/swizec/7898"],
  "categories": "Business, Opinions, Personal, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Tried VSCode last night, spent 18 minutes yak shaving my config, couldn't make it visually pleasant, moved back to Emacs. Everyone raves about VSCode, and I'm sure it's a great editor/IDE, but it's just not for me.`}</p>
    <p>{`Maybe I'm holding it wrong. You tell me. I `}<em parentName="p">{`want`}</em>{` to give it another chance.`}</p>
    <p>{`Here's what I didn't like 👇`}</p>
    <p>{`In Emacs, I use the Tomorrow theme, I think. My coding OS (that's an old timey joke, btw) looks like this.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/emacs-1.png",
        "alt": null
      }}></img></p>
    <p>{`There's not much going on, and I like that. The code looks pleasant, the background is white. Nothing to the left or right of the code.`}</p>
    <p>{`The window header tells me my file's full path, and there's a narrow strip of file tabs. Tabs filter based on file mode so when I'm in JavaScript, I only see JavaScript files, in Ruby only Ruby, in CSS only CSS, etc. This is neat when you're working on large projects with many different file types.`}</p>
    <p>{`At the bottom, you have a small command line. This is where my `}<em parentName="p">{`"Find open file using fuzzy search"`}</em>{` lives. Makes it easy to navigate when there are 50 different files open. You can use it to fuzzy search un-open files too.`}</p>
    <p>{`I rarely close my editor and often forget to close files.`}</p>
    <p>{`Also at the bottom, you have a git indicator that tells you which branch you're on, whether the current file is dirty, which mode you're in, etc.`}</p>
    <h2 {...{
      "id": "vscode--visual-clutter"
    }}>{`VSCode – visual clutter`}</h2>
    <p>{`Here's what that same file looks like in VSCode.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-1.png",
        "alt": null
      }}></img></p>
    <p>{`Using the same (I think?) Tomorrow light theme, but it feels less clean. It feels like too many colors pop, and the font looks less pleasant.`}</p>
    <p>{`This is just habit, however. I could get used to it quick.`}</p>
    <p>{`What I don't like is all the visual cruft that VSCode puts on my window.`}</p>
    <p>{`There's the weird gigantic scrollbar they took from Sublime. Why do I need a zoomed out version of my file cluttering up my view?`}</p>
    <p>{`The tabs are also bigger, and there's buttons top right that I think should exist solely as keyboard shortcuts. Two of them (the find and the 3 dots) don't do anything. The split view is nice, maybe.`}</p>
    <p>{`On the bottom, you have basically the same information that my Emacs provides, but on a brighter poppier background. I think that makes it look distracting.`}</p>
    <p>{`My code should be the most visually pronounced part of my editor. Not my editor's tools.`}</p>
    <p>{`To the left, we have an atrocity: A project explorer.`}</p>
    <p>{`Yes, you can close it. Then VSCode looks like this 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-2.png",
        "alt": null
      }}></img></p>
    <p>{`Better, but still cluttered. `}<em parentName="p">{`grump`}</em></p>
    <h2 {...{
      "id": "vscode--project-explorer"
    }}>{`VSCode – "project explorer"`}</h2>
    <p>{`Fundamentally what I don't like about the concept of a "project explorer" is that it promotes the concept of adding projects to your editor. They clutter up for years and why does my editor care anyway?`}</p>
    <p>{`Yes, to give me helpful search functions and code explanations and stuff. All of that is great.`}</p>
    <p>{`But it's also implicit.`}</p>
    <p>{`Emacs looks at your current directory and provides fuzzy search through files within. VSCode takes a whole directory tree and provides fuzzy search.`}</p>
    <p>{`That might be better. I don't know. I've seen my coworkers have fun times when they try to open `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` and their IDE helpfully suggests 50 different `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` files.`}</p>
    <p>{`Are we meant to have unique filenames throughout our project? Then what's the point of structuring it in directories at all?`}</p>
    <p>{`Then there's the uncloseable toolbar to the left.`}</p>
    <p>{`Why does the `}<inlineCode parentName="p">{`Extensions`}</inlineCode>{` button have to always be visible? How often do people fiddle with their extensions?`}</p>
    <p>{`Why is `}<inlineCode parentName="p">{`Search`}</inlineCode>{` always there? Should be just a shortcut everyone learns within 3min of using an editor.`}</p>
    <p>{`The debugger is nice.`}</p>
    <p>{`Why do I need source control stuff right there always bugging me?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-3.png",
        "alt": null
      }}></img></p>
    <p>{`Why is it trying to show stuff from multiple git repos at the same time? I'm only working on one project, yo.`}</p>
    <p>{`Actually, that might be my bad. I seem to have run `}<inlineCode parentName="p">{`git init`}</inlineCode>{` on my home directory at some point. Now VSCode thinks everything is a project and gets confused.`}</p>
    <p>{`It also complains that 5000+ unstaged files might be too much.`}</p>
    <p>{`Emacs wouldn't even think about poking that. Doesn't think about projects you see. Oh, you're in a file? Here you go. Oh ,you want to search the directory? Sure.`}</p>
    <h2 {...{
      "id": "vscode--trying-to-be-too-helpful"
    }}>{`VSCode – trying to be too helpful`}</h2>
    <p>{`And then there's this stuff. It tries to be helpful and I'm sure for some people it works really well and helps a lot.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-4.png",
        "alt": null
      }}></img></p>
    <p>{`Thank you, VSCode. I had no idea that `}<inlineCode parentName="p">{`<Promises>`}</inlineCode>{` was a JSX element. Very helpful advice right there.`}</p>
    <p>{`And `}<inlineCode parentName="p">{`key=""`}</inlineCode>{` is an attribute! How wonderful, I never would have guessed.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-5.png",
        "alt": null
      }}></img></p>
    <p>{`Maybe I'm holding it wrong. Maybe in bigger contexts, this is super useful, or maybe I need TypeScript to make it shine.`}</p>
    <p>{`Right now, it's just annoying that I have to be super careful about where I put my mouse lest some tiny popup pops up and obstructs my code.`}</p>
    <p>{`I guess the `}<inlineCode parentName="p">{`Peek Definition`}</inlineCode>{` option is nice. But whose fingers are stretchy enough to press alt-F12 comfortably?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/vscode-6.png",
        "alt": null
      }}></img></p>
    <p>{`Yeah, I can see that being useful in a large codebase.`}</p>
    <p>{`Oh, and VSCode does that annoying thing where you write a `}<inlineCode parentName="p">{`(`}</inlineCode>{` and it adds a `}<inlineCode parentName="p">{`)`}</inlineCode>{`. I've always found that infuriating ever since the first editors started doing it in the early aughts. Stop that.`}</p>
    <p>{`At the same time, it doesn't autocomplete HTML and JSX tags. I got used to typing `}<inlineCode parentName="p">{`</`}</inlineCode>{` and Emacs automatically closing my open tag. That is nice, but VSCode doesn't do it.`}</p>
    <p>{`Maybe there's a plugin I'm missing?`}</p>
    <h2 {...{
      "id": "why-im-not-switching-yet"
    }}>{`Why I'm not switching yet`}</h2>
    <p>{`Habit.`}</p>
    <p>{`VSCode has plenty of features that my Emacs setup does not. It's a great editor, and I can see myself growing to like it.`}</p>
    <p>{`But it just doesn't offer any killer features to make me invest the time to get over the little niggles that annoy me.`}</p>
    <p>{`Unless you can suggest plugins I'm missing to fix those? `}<a parentName="p" {...{
        "href": "http://twitter.com/home?status=.@swizec's%20VSCode%20opinion%20is%20dumb%20because:%20%20https://swizec.com/blog/wont-be-switching-vscode/swizec/7898"
      }}>{`Shout at me on Twitter`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      